import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { formatDateTime } from "@/lib/format";
import {
  getMerosVerlauf,
  merosOccupiedDays,
  merosPlanBars,
  merosZustandLabel,
  type MerosVerlaufSegment,
} from "@/lib/meros";

const OBEN_FARBE = "#0a84ff";
const UNTEN_FARBE = "#8e8e93";

/** Zustandsbalken in der Meros-Zeile. Liegt hinter den Einsätzen und spart deren Tage aus. */
export function MerosStateBars({
  days,
  dayWidth,
  barHeight,
  barTop,
  assignments = [],
}: {
  days: Date[];
  dayWidth: number;
  barHeight: number;
  barTop: number;
  assignments?: Array<{ date_from: string; date_to?: string | null }>;
}) {
  const [verlauf, setVerlauf] = useState<MerosVerlaufSegment[]>([]);
  const [farbeOben, setFarbeOben] = useState(OBEN_FARBE);
  const [farbeUnten, setFarbeUnten] = useState(UNTEN_FARBE);

  useEffect(() => {
    let cancelled = false;
    const pull = () => {
      getMerosVerlauf()
        .then((data) => {
          if (cancelled) return;
          setVerlauf(data.verlauf || []);
          if (data.farbe_oben) setFarbeOben(data.farbe_oben);
          if (data.farbe_unten) setFarbeUnten(data.farbe_unten);
        })
        .catch(() => {
          if (!cancelled) setVerlauf([]);
        });
    };
    pull();
    const timer = window.setInterval(pull, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const occupied = useMemo(() => merosOccupiedDays(assignments), [assignments]);
  const bars = merosPlanBars(verlauf, days, occupied);
  const barSize = Math.max(16, barHeight);

  return (
    <>
      {bars.map((bar) => {
        const inset = bar.span === 1 ? 1 : 2;
        const width = Math.max(8, bar.span * dayWidth - inset * 2);
        const narrow = width < 46;
        const label = narrow
          ? bar.zustand === "oben"
            ? "O"
            : "U"
          : merosZustandLabel(bar.zustand);
        const seit = formatDateTime(bar.von);
        const title =
          bar.art === "klapp"
            ? `Geklappt am ${seit}, danach ${merosZustandLabel(bar.zustand)}`
            : `Seit ${seit} ${merosZustandLabel(bar.zustand)}`;
        return (
          <div
            key={`${bar.von}-${bar.startIdx}`}
            className={clsx("mp-bar mp-meros-bar", bar.span === 1 && "mp-bar-single-day")}
            style={{
              left: bar.startIdx * dayWidth + inset,
              width,
              top: barTop,
              height: barSize,
              backgroundColor: bar.zustand === "oben" ? farbeOben : farbeUnten,
              zIndex: 0,
              pointerEvents: "none",
            }}
            title={bar.notiz ? `${title}. ${bar.notiz}` : title}
          >
            <span className="mp-bar-label">{label}</span>
          </div>
        );
      })}
    </>
  );
}
